import { find } from 'lodash';
import { add, bignumber, evaluate } from 'mathjs';
import { useCallback } from 'react';
import { Money } from 'sr-types/lib/currency/v1/graphql';
import { CalculationType, LineCalculationInfo } from 'sr-types/lib/opportunity/v1/graphql';
import { Attribute, AvailableTypeEnum } from '../../../attributes/attributes';
import { FIELD_VARIABLE_MATCHER, REPLACE_KEY_IN_FORMULA_REGEX } from '../../../pricing/PricingConstants';
import { useBrowserOrAccountLocale } from '../../hooks/useBrowserOrAccountLocale';
import { log } from '../../utils/commonUtils';
import { isMoney, moneyToBigNumber, numberToMoney } from '../../utils/money';
import { parseNumber } from '../../utils/numbers';
import PricingLine from './PricingLine';
/**
 * Interface for performing cost calculations and evaluating pricing expressions.
 *
 * @remarks
 * This interface defines the contract for a cost calculator, which can calculate pricing lines,
 * evaluate expressions based on calculation details, and optionally process attribute values for calculation types.
 */

export interface CostCalculator {
    /**
     * Calculates and returns a new PricingLine based on the provided row.
     * @param row - The pricing line to be calculated.
     * @returns The calculated PricingLine.
     */
    calculate(row: PricingLine): PricingLine;
    /**
     * Evaluates an expression using the provided calculation details and returns the result.
     * @param details - The details required to evaluate the expression.
     * @returns The result of the evaluated expression.
     */
    evaluateExpressionWithFields(details: CalculationDetails, locale: string, defaultCurrencyCode?: string): any;
    /**
     * (Optional) Calculates values for attributes based on calculation type, using external field mappings and cost columns.
     * @param attributes - The list of attributes to process.
     * @param externalFieldMap - A mapping of external field names to their values.
     * @param costColumns - The list of cost column names relevant to the calculation.
     * @returns The updated list of attributes with calculated values.
     */
    calculateValueForCalcTypeAttributes?: (
        attributes: Attribute[],
        externalFieldMap: Record<string, any>,
        costColumns: String[]
    ) => Attribute[];
}

export enum Type {
    NUM = 'num',
    STR = 'str',
    MONEY = 'money'
}

const detectType = (obj: any): Type | undefined => {
    if (isMoney(obj)) {
        return Type.MONEY;
    } else if (typeof obj === 'string') {
        return Type.STR;
    } else if (typeof obj === 'number') {
        return Type.NUM;
    } else {
        return undefined;
    }
};

export interface CalculationDetails {
    calculationFormula: string;
    dependentFields: Record<string, any>;
    resultType: string;
}

export default function useCalculator(
    defaultCurrencyCode: string,
    calculationTypes: CalculationType[],
    shouldCalculate = true
): CostCalculator {
    const locale = useBrowserOrAccountLocale();
    const calc = useCallback(
        (row: PricingLine) => {
            const costLineCalculationType: CalculationType =
                calculationTypes && row.calculationType
                    ? find(calculationTypes, { code: row.calculationType })
                    : undefined;

            // this calculation type will come from service in future
            const discountCalcType = {
                formula: 'baseTotal - discounts',
                label: 'Discounted Total',
                code: 'dt',
                assignTo: 'discountedTotal'
            };
            const allCalculationTypes = [costLineCalculationType];
            if (row.lineType === 'CostLine') {
                allCalculationTypes.push(discountCalcType);
            }
            let calculatedRow = row;
            allCalculationTypes.map((calculationType) => {
                if (shouldCalculate && calculationType) {
                    let formula = calculationType.formula;
                    const fieldNames: string[] = formula.match(FIELD_VARIABLE_MATCHER) || undefined;
                    if (
                        fieldNames &&
                        fieldNames.every(
                            (field) => calculatedRow.hasOwnProperty(field) && calculatedRow[field] !== undefined
                        )
                    ) {
                        const assignTo = calculationType.assignTo;
                        let hasValidValues = true;
                        try {
                            for (let key in calculatedRow) {
                                if (formula.includes(key)) {
                                    const part = calculatedRow[key];
                                    const type: Type = detectType(part);
                                    if (part === undefined || part === null || type === undefined) {
                                        hasValidValues = false;
                                        continue;
                                    }
                                    let parsed: string = '';
                                    switch (type) {
                                        case Type.MONEY:
                                            parsed = moneyToBigNumber(part).toString();
                                            break;
                                        case Type.NUM:
                                            parsed = part.toString();
                                            break;
                                        case Type.STR:
                                            parsed = parseNumber(part, { locale }).parsed.toString();
                                            break;
                                    }
                                    if (parsed.length) {
                                        formula = formula.replace(key, parsed);
                                    }
                                }
                            }
                            if (hasValidValues) {
                                const result = evaluate(formula);
                                const money = numberToMoney(result, defaultCurrencyCode);
                                calculatedRow = { ...calculatedRow, [assignTo]: money };
                            }
                        } catch (error) {
                            log(`Formula ${formula} failed to evaluate:`, error.message);
                            calculatedRow = { ...calculatedRow, [assignTo]: undefined };
                        }
                    }
                }
            });
            return calculatedRow;
        },
        [calculationTypes, defaultCurrencyCode, locale, shouldCalculate]
    );

    const calcValueForCalcTypeAttrs = (
        attributes: Attribute[],
        externalFieldMap: Record<string, any>,
        costColumns: String[]
    ) => {
        let attributeValueMap: { [key: string]: any } = {};
        return attributes.map((attr) => {
            if (attr?.fieldType === AvailableTypeEnum.calculationType) {
                // Parse metadata JSON string into CalculationType object
                const calculationType: CalculationType =
                    typeof attr.metadata === 'string' ? JSON.parse(attr.metadata) : attr.metadata;

                const fieldNames: string[] = calculationType.formula.match(FIELD_VARIABLE_MATCHER) || undefined;
                const dependentFields = fieldNames.reduce(
                    (acc, name) => {
                        // Check in attributeValueMap first
                        if (attributeValueMap.hasOwnProperty(name)) {
                            acc[name] = attributeValueMap[name];
                        } else if (externalFieldMap.hasOwnProperty(name)) {
                            acc[name] = externalFieldMap[name];
                        } else {
                            acc[name] = undefined;
                        }
                        return acc;
                    },
                    {} as Record<string, any>
                );
                const resultType = costColumns.includes(attr.name) ? Type.MONEY : Type.NUM;
                const value = evaluateExpWithFields(
                    {
                        calculationFormula: calculationType.formula,
                        dependentFields,
                        resultType
                    },
                    defaultCurrencyCode,
                    locale
                );
                attributeValueMap[attr.name] = value;
                return { ...attr, value };
            }
            return attr;
        });
    };
    return {
        calculate: calc,
        evaluateExpressionWithFields: evaluateExpWithFields,
        calculateValueForCalcTypeAttributes: calcValueForCalcTypeAttrs
    };
};

export const evaluateExpWithFields = (details: CalculationDetails, locale: string, defaultCurrencyCode?: string) => {
    if (!details.calculationFormula || !details.dependentFields) {
        return;
    }

    let formula = details.calculationFormula;
    try {
        const fieldNames: string[] = formula.match(FIELD_VARIABLE_MATCHER) || [];
        let hasValidValues = fieldNames.every(
            (field) =>
            Object.prototype.hasOwnProperty.call(details.dependentFields, field) &&
            details.dependentFields[field] !== undefined &&
            details.dependentFields[field] !== null
        );

        for (const key of fieldNames) {
            const value = details.dependentFields[key];
            const type = detectType(value);
            if (value === undefined || value === null || type === undefined) {
                hasValidValues = false;
                continue;
            }
            let parsed = '';
            switch (type) {
                case Type.MONEY:
                    parsed = moneyToBigNumber(value).toString();
                    break;
                case Type.NUM:
                    parsed = value.toString();
                    break;
                case Type.STR:
                    parsed = parseNumber(value, { locale }).parsed.toString();
                    break;
            }
            if (parsed.length) {
                formula = formula.replace(REPLACE_KEY_IN_FORMULA_REGEX(key), parsed);
            }
        }

        if (!hasValidValues) {
            return undefined;
        }

        const result = evaluate(formula);
        switch (details.resultType) {
            case Type.MONEY:
            return numberToMoney(result, defaultCurrencyCode);
            case Type.NUM:
            return Number(result);
            case Type.STR:
            return String(result);
            default:
            return result;
        }
    } catch (error) {
        log(`Formula ${formula} failed to evaluate:`, error.message);
        return undefined;
    }
};

export const getSumOfDiscounts = (adjustmentsData, lineCurrency): Money => {
    let sumOfDiscounts = bignumber(0);
    adjustmentsData.map((adjustment: LineCalculationInfo) => {
        sumOfDiscounts = add(
            sumOfDiscounts,
            isMoney(adjustment.calculationInfo.baseTotal)
                ? moneyToBigNumber(adjustment.calculationInfo.baseTotal)
                : bignumber(0)
        );
    });
    return numberToMoney(sumOfDiscounts, lineCurrency);
};
